
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  section: 'Transfer',
  title: 'Transfer Dashboard',
  description: 'Learn about the features available in the Plaid Transfer dashboard.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Transfer Dashboard' subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`You can complete most transfer operational activities in the Transfer Dashboard. Many of these actions are also available via the `}<a parentName="p" {...{
        "href": "/docs/api/products/transfer/"
      }}>{`API`}</a>{`.`}</p>
    <p>{`A link to the Transfer Dashboard will appear in the Dashboard navigation after you have applied for access to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`Transfer in the Production environment`}</a>{`. You can also access the Transfer Dashboard before applying for Production access by navigating directly to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/transfer"
      }}>{`dashboard.plaid.com/transfer`}</a>{`.`}</p>
    <p>{`The main overview page displays the following:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Aggregate metrics`}</strong>{`: total transfers, return rates, current volumes`}</li>
      <li parentName="ul"><strong parentName="li">{`Account information`}</strong>{`: limits (daily, monthly) and utilization to date. For ACH transfers, this also includes SEC codes you are approved for.`}</li>
      <li parentName="ul"><strong parentName="li">{`Transfer activity`}</strong>{`: table displaying all transfer, refund and sweep activity`}</li>
    </ul>
    <Image src='/assets/img/docs/transfer/dashboard_home.png' alt="Plaid Transfer Dashboard home page showing a program snapshot of transfers initiated, posted, and returned, ACH debit return rates, debit and credit limit utilization, and a processing volume chart." caption='Home page of Transfer dashboard' expandable={true} mdxType="Image" />
    <p>{`Features available through the Dashboard include:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/transfer/flow-of-funds/#adding-or-changing-funding-accounts"
        }}>{`Viewing and changing funding account information`}</a>{`: You can self-serve linking a new funding account, deleting an account, or changing the default status of a funding account in the "Account details" page of the dashboard`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/transfer/flow-of-funds/#moving-money-via-the-dashboard"
        }}>{`Moving money between your funding account and your Plaid Ledger`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/transfer/refunds/"
        }}>{`Refunding`}</a>{` or `}<a parentName="li" {...{
          "href": "/docs/transfer/creating-transfers/#canceling-transfers"
        }}>{`canceling`}</a>{` transfers`}</li>
      <li parentName="ul">{`Viewing and searching transfer details including events, statuses, and associated refunds, and viewing the trace numbers and event history of a transfer`}</li>
      <li parentName="ul">{`Configuring `}<a parentName="li" {...{
          "href": "/docs/transfer/signal-rules"
        }}>{`Signal rules`}</a></li>
    </ul>
    <Callout mdxType="Callout">
      <p>{`Creating and authorizing a transfer `}<em parentName="p">{`cannot`}</em>{` be completed from the Dashboard, nor can you link an end user's bank account from the Dashboard. These operations can only be completed using the API.`}</p>
    </Callout>
    <Image src='/assets/img/docs/transfer/transfer-details.png' alt="Transfer details page showing a $0.01 debit in a Funds available state, with originator, customer, and bank information, guaranteed decision status, and an activity log." caption='Transfer details page, including originator, customer, and bank information, guaranteed decision status, and options to view logs, issue a refund, and view associated refunds.' expandable={true} mdxType="Image" />
    <h4 {...{
      "id": "limits-reserves-and-hold-time"
    }}>{`Limits, reserves, and hold time`}</h4>
    <p>{`Your initial limits, reserve requirements, and debit hold time (if applicable) are set at onboarding. As your business grows over time, you can request changes to your debit limits, credit limits, and/or debit hold time through the dashboard.`}</p>
    <Image src='/assets/img/docs/transfer/request-limit-change.png' alt="Plaid Transfer Dashboard. Enter projected debit and credit volumes for limit change request. Options include single, daily, monthly." caption='Plaid Transfer Dashboard Limits Change Request' expandable={true} mdxType="Image" />
    <p>{`If your requested changes require additional reserves, the amount will be indicated to you. Once the additional reserves are sent to Plaid (if applicable), the approved changes will be automatically implemented.`}</p>
    <p>{`You can view your current limits, reserves, and debit hold time in the "Account Details" section of the dashboard:`}</p>
    <Image src='/assets/img/docs/transfer/limits-and-hold-times.png' alt="Plaid Transfer Dashboard showing account details, funding accounts, approved limits, hold times, and options to deposit or withdraw." caption='Plaid Transfer Dashboard Limits and Hold Times' expandable={true} mdxType="Image" />
    <p>{`Wire instructions for funding the reserve account can also be found by clicking "Wire instructions" on the "Account details" section.`}</p>
    <h4 {...{
      "id": "ledger-management"
    }}>{`Ledger management`}</h4>
    <p>{`From the Dashboard, you can see more details of your Ledger, add or withdraw money, and configure settings such as automated cashouts. For details, see `}<a parentName="p" {...{
        "href": "/docs/transfer/flow-of-funds/#moving-money-via-the-dashboard"
      }}>{`Moving money via the Dashboard`}</a>{`.`}</p>
    <h4 {...{
      "id": "report-extraction"
    }}>{`Report extraction`}</h4>
    <h5 {...{
      "id": "transfer-activity-report"
    }}>{`Transfer Activity Report`}</h5>
    <p>{`In the Overview page of the Transfer section of the Plaid Dashboard, you can export a report with a list of transfers and sweeps as a .csv file by clicking "Export CSV." `}</p>
    <Image src='/assets/img/docs/transfer/csv.png' alt="Popup for downloading transfers as CSV. Options: 'Yes, continue' and 'Cancel'. Background: transfer metrics and activity log." caption='Plaid Transfer Dashboard CSV Export' expandable={true} mdxType="Image" />
    <p>{`Filters can be applied to the report by selecting `}<strong parentName="p">{`Filter`}</strong>{`. `}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Date`}</strong>{`: Only transfers and sweeps that were created during the specified period (have `}<inlineCode parentName="li">{`created_at`}</inlineCode>{` timestamps within the date range) will be included`}</li>
      <li parentName="ul"><strong parentName="li">{`Transaction type`}</strong>{`: Only the selected transaction types (transfers, sweeps, and/or refunds) will be included`}</li>
      <li parentName="ul"><strong parentName="li">{`Status`}</strong>{`: Only transfers or sweeps that currently (as of the report creation time) have the selected status(es) (`}<inlineCode parentName="li">{`pending`}</inlineCode>{`, `}<inlineCode parentName="li">{`posted`}</inlineCode>{`, `}<inlineCode parentName="li">{`settled`}</inlineCode>{`, `}<inlineCode parentName="li">{`funds available`}</inlineCode>{`, `}<inlineCode parentName="li">{`returned`}</inlineCode>{`, `}<inlineCode parentName="li">{`failed`}</inlineCode>{`, and/or `}<inlineCode parentName="li">{`cancelled`}</inlineCode>{`) will be included`}</li>
    </ul>
    <p><strong parentName="p">{`In this report, you will see the following columns:`}</strong>{`
`}<em parentName="p">{`Note:`}</em>{` All timestamps utilize the timezone (EST or UTC) applied to the report.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Column`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`ID`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The ID of the transaction type that occurred. For example, if the Type is `}<inlineCode parentName="td">{`transfer`}</inlineCode>{`, then this field is the transfer ID.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Status`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The status of the transaction type as of the time of the report export. Note that `}<inlineCode parentName="td">{`funds available`}</inlineCode>{` is a status that is only applicable to debit transfers or debit sweeps.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Type`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Whether the transaction was a `}<inlineCode parentName="td">{`debit`}</inlineCode>{` or `}<inlineCode parentName="td">{`credit`}</inlineCode>{` payment instruction. Transfers and sweeps can be debits or credits. Refunds are credits only.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Amount`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The amount of the transaction. Amounts are signed. Debits have positive signed amounts (as debit transfers and sweeps increase the ledger balance). Credits have negative signed amounts (this activity decreases the ledger balance).`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`ACH class`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`For activity that was executed via the ACH network, the ACH Standard Entry Class (SEC) code of the transaction. For more details, see `}<a parentName="td" {...{
              "href": "/docs/transfer/creating-transfers/#ach-sec-codes"
            }}>{`ACH SEC codes`}</a>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Network`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The payment network used to fulfill the transaction. For debits, this will be either `}<inlineCode parentName="td">{`same-day-ach`}</inlineCode>{` or `}<inlineCode parentName="td">{`ach`}</inlineCode>{` (Standard ACH). For credits, this can be `}<inlineCode parentName="td">{`rtp`}</inlineCode>{`, `}<inlineCode parentName="td">{`wire`}</inlineCode>{`, `}<inlineCode parentName="td">{`same-day-ach`}</inlineCode>{`, or `}<inlineCode parentName="td">{`ach`}</inlineCode>{`. Note that if a Same Day ACH transfer was submitted after the cutoff, Transfer will downgrade the network to Standard ACH. Sweeps are not downgraded, so if a Same Day ACH cutoff is missed, the sweep will be retried the next morning for the first Same Day ACH cutoff of the day.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Account_id`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Plaid's unique identifier for the account.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Account type`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The type of bank account being debited or credited for a transfer. Either `}<inlineCode parentName="td">{`checking`}</inlineCode>{` or `}<inlineCode parentName="td">{`savings`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`User Legal Name`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The legal name of the end user being debited or credited. Only populated for transfers and refunds.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`User Email Address`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The email address of the end user being debited or credited. Only populated for transfers and refunds.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`User Routing Number`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The routing number of the bank account being debited or credited.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Failure reason - ACH return code`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`If an ACH or Same Day ACH transfer has failed (returned), the return code (RXX).`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Failure reason - Description`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`If an ACH or Same Day ACH transfer has failed (returned), a description for the return code.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Timestamps:  Created / Pending / Posted / Settled / Funds Available / Returned / Failed / Cancelled`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The timestamp of when the transfer, refund, or sweep progressed to this status.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Metadata`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The contents of the key-value pair(s) included for the transaction (if applicable).`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Refund Of`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`If the transaction is a refund, the ID of the debit transfer that the transaction is refunding`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Refunded Amount`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Refund amount issued. Refunds may be issued partially or completely.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Refund 1, 2, 3`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`ID of the refund(s) issued. Multiple refunds can only be issued if the initial refund was not a complete refund of the debit transfer.`}</td>
        </tr>
      </tbody>
    </table>
    <p><em parentName="p">{`Note:`}</em>{` The following columns are not applicable for customers on Plaid Ledger:  `}<inlineCode parentName="p">{`swept`}</inlineCode>{`, `}<inlineCode parentName="p">{`return_swept`}</inlineCode>{`, `}<inlineCode parentName="p">{`swept_settled`}</inlineCode>{`, `}<inlineCode parentName="p">{`signed_sweep_amount`}</inlineCode>{`, `}<inlineCode parentName="p">{`reserve_swept`}</inlineCode>{`, `}<inlineCode parentName="p">{`signed_reserve_sweep_amount`}</inlineCode>{`, `}<inlineCode parentName="p">{`signed_return_sweep_amount`}</inlineCode>{`. `}</p>
    <h5 {...{
      "id": "ledger-balance-report"
    }}>{`Ledger balance Report`}</h5>
    <p>{`Plaid provides reconciliation reports of all money moved into and out of your Plaid Ledger settlement balance. You can generate a custom report for any date range (up to 31 days) via the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/transfer/account-details"
      }}>{`Transfer account details`}</a>{` Dashboard page. `}</p>
    <p>{`In this report, you will see the following columns:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Column`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Timestamp`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The datetime, in the Eastern time zone, when the balance was impacted.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Originator Client ID / Client Name`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Denotes the owner of the Plaid Ledger balance. For customers running a payments `}<a parentName="td" {...{
              "href": "/docs/transfer/platform-payments/"
            }}>{`platform`}</a>{`, this will be your subcustomer's client ID and business name. Otherwise, it's your own client ID and business name.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Type`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The type of activity impacting the balance:`}<br /><inlineCode parentName="td">{`transfer`}</inlineCode>{` indicates activity between the end consumer's bank account (i.e. accounts linked in your application's product flows) and the Ledger balance.`}<br /><inlineCode parentName="td">{`deposit/withdrawal`}</inlineCode>{` indicates activity between your business checking accounts and the Ledger balance.`}<br /><inlineCode parentName="td">{`refund`}</inlineCode>{`s can occur on ACH debits to return funds to the consumer account.`}<br /><inlineCode parentName="td">{`distribution`}</inlineCode>{` indicates a movement of funds from one Plaid Ledger's available balance to another Plaid Ledger's available balance.`}<br /><inlineCode parentName="td">{`manual_adjustment`}</inlineCode>{` indicates this balance change was entered manually by Plaid.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`ID`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The ID of the activity that occurred. For example, if the Type is `}<inlineCode parentName="td">{`transfer`}</inlineCode>{`, then this field is the transfer ID.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Description`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A summary of the action that took place on this Type.`}<br /><inlineCode parentName="td">{`originated`}</inlineCode>{`: A request to originate the transfer, sweep, refund or distribute was received by Plaid.`}<br /><inlineCode parentName="td">{`returned`}</inlineCode>{`: An ACH return occurred.`}<br /><inlineCode parentName="td">{`canceled`}</inlineCode>{`: Payment execution was canceled via the API or dashboard.`}<br /><inlineCode parentName="td">{`converted_to_available`}</inlineCode>{`: Funds that were originally placed in a pending balance were moved to the available balance.`}<br /><inlineCode parentName="td">{`failed`}</inlineCode>{`: The attempt to move money failed.`}<br /><inlineCode parentName="td">{`dishonor`}</inlineCode>{`: a previous ACH return that came in was invalidated. When a dishonored return occurs, the transfer status will not change, but a row will be added to the report showing the funds from the dishonored return being added back to the Ledger balance. `}<br /><inlineCode parentName="td">{`facilitator_fee`}</inlineCode>{`: A fee was charged by the platform parent to its subcustomer.`}<br /><inlineCode parentName="td">{`distribute`}</inlineCode>{`: Funds were moved from one Plaid Ledger's `}<inlineCode parentName="td">{`available`}</inlineCode>{` balance to another Plaid Ledger's `}<inlineCode parentName="td">{`available`}</inlineCode>{` balance.`}<br /><inlineCode parentName="td">{`manual_adjustment`}</inlineCode>{`: A balance adjustment was entered manually.`}<br /><inlineCode parentName="td">{`incentive`}</inlineCode>{`: If "incentive" is prepended to a value above, it is referencing a pay by bank incentive that occurred on the transfer (for example, `}<inlineCode parentName="td">{`incentive originated`}</inlineCode>{`).`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Impacted Balance Type`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The Ledger balance type that was impacted: `}<inlineCode parentName="td">{`available`}</inlineCode>{`, `}<inlineCode parentName="td">{`pending`}</inlineCode>{`, or `}<inlineCode parentName="td">{`pending_to_available`}</inlineCode>{`. The latter denotes that both balances were impacted during a move of funds from `}<inlineCode parentName="td">{`pending`}</inlineCode>{` to `}<inlineCode parentName="td">{`available`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Amount`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The signed amount of the balance impact. `}<inlineCode parentName="td">{`pending_to_available`}</inlineCode>{` is always a positive amount, noting the amount removed from `}<inlineCode parentName="td">{`pending`}</inlineCode>{` and allocated to `}<inlineCode parentName="td">{`available`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`New Pending Balance`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The resulting value of the `}<inlineCode parentName="td">{`pending`}</inlineCode>{` balance after this activity was applied to the relevant balance(s).`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`New Available Balance`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The resulting value of the `}<inlineCode parentName="td">{`available`}</inlineCode>{` balance after this activity was applied to the relevant balance(s).`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Using these columns, you can complete comprehensive financial reconciliation. For example: to see all the activity between your business checking account and the Ledger balance, filter for all rows where the column "Type" is `}<inlineCode parentName="p">{`deposit/withdrawal`}</inlineCode>{`. To see refunds issued, filter for all rows where the column "Type" is `}<inlineCode parentName="p">{`refund`}</inlineCode>{`. To see exactly when a specific ACH debit transfer's funds became available in the Ledger balance, search for the row of type `}<inlineCode parentName="p">{`transfer`}</inlineCode>{`, specifying the relevant `}<inlineCode parentName="p">{`ID`}</inlineCode>{`, and look for its `}<inlineCode parentName="p">{`pending_to_available`}</inlineCode>{` balance impact type.`}</p>
    <h5 {...{
      "id": "email-reconciliation-reports"
    }}>{`Email reconciliation reports`}</h5>
    <p>{`Plaid also provides optional monthly reconciliation reports via email, including aggregated details of funds movement activity for the month. To opt in to receiving these reports, contact your account manager.`}</p>
    <h4 {...{
      "id": "rules"
    }}>{`Rules`}</h4>
    <p>{`See `}<a parentName="p" {...{
        "href": "/docs/transfer/signal-rules"
      }}>{`Using Signal with Transfer`}</a>{` to learn how to configure the rules that will be run during authorization checks for debit transfers.`}</p>
    <h4 {...{
      "id": "tasks"
    }}>{`Tasks`}</h4>
    <p>{`The Tasks pane will show any onboarding tasks you have not yet completed, such as selecting a hold time or sending your initial reserve amounts. `}</p>
    <p>{`If Plaid is requesting that you provide `}<a parentName="p" {...{
        "href": "/docs/transfer/creating-transfers/#managing-proof-of-authorization"
      }}>{`Proof of Authorization`}</a>{` for any disputed transactions, this will also be shown on the Tasks pane. `}</p>
    <h4 {...{
      "id": "dashboard-permissions"
    }}>{`Dashboard permissions`}</h4>
    <p>{`Transfer-specific Dashboard permissions can be set via the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/team/members"
      }}>{`Settings > Members`}</a>{` page. Transfer-specific permissions are as follows:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Transfer Read & Write All`}</strong>{` - Grants access to all Transfer features.`}
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`Transfer Read`}</strong>{` - Grants read access to Transfer data and reports`}</li>
          <li parentName="ul"><strong parentName="li">{`Manage Funding Accounts`}</strong>{` - Grants ability to manage Transfer funding accounts`}</li>
          <li parentName="ul"><strong parentName="li">{`Manage Ledgers`}</strong>{` - Grants ability to manage ledgers, including automated balance management settings`}</li>
          <li parentName="ul"><strong parentName="li">{`Manage Transfer Limits`}</strong>{` - Grants ability to request changes to limits and/or hold times`}</li>
          <li parentName="ul"><strong parentName="li">{`Initiate Refunds and Cancel Transfers`}</strong>{` - Grants ability to initiate refunds and cancel transfers`}</li>
          <li parentName="ul"><strong parentName="li">{`Move Transfer Ledger Funds`}</strong>{` - Grants ability to deposit and withdraw funds to/from Transfer Ledger`}</li>
        </ul>
      </li>
    </ul>
    <p>{`Configuring Signal rules additionally requires the `}<strong parentName="p">{`Rule Service Write Access`}</strong>{` permission, which grants access to create and update rules and rulesets to automate transaction authorization. This permission is granted under Signal Dashboard Permissions rather than Transfer permissions, so it is not included in `}<strong parentName="p">{`Transfer Read & Write All`}</strong>{`.`}</p>
    <p>{`Note that these permissions only impact Dashboard access and do not apply to actions initiated via the API. A user who has the "Production Keys" permission or otherwise has access to Plaid Production API keys can still use the API to move funds in and out of the Ledger or initiate a refund, regardless of their Transfer Dashboard permissions.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "limits-reserves-and-hold-time",
        level: 4,
        title: "Limits, reserves, and hold time",
      },
{
        id: "ledger-management",
        level: 4,
        title: "Ledger management",
      },
{
        id: "report-extraction",
        level: 4,
        title: "Report extraction",
      },
{
        id: "rules",
        level: 4,
        title: "Rules",
      },
{
        id: "tasks",
        level: 4,
        title: "Tasks",
      },
{
        id: "dashboard-permissions",
        level: 4,
        title: "Dashboard permissions",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
